import { FunctionComponent, ReactNode, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import cx from 'classnames'
import url from 'libraries/front-end-core/core/url'
import BrokenRefDialog from 'components/BrokenRefDialog'
import { RESPONSE_PENDING } from 'libraries/front-end-core/constants/responses'
import { isValidResponse } from 'libraries/front-end-core/helpers/dataflow'
import useObservable from 'libraries/front-end-core/hooks/useObservable'
import { QA_CONTENT } from 'constants/controllers'
import { requestSections } from 'services/QAService'
import selectFlavour from 'redux/selectors/selectFlavour'
import selectIsLogged from 'redux/selectors/selectIsLogged'
import { setUserRolesModalIsOpen } from 'redux/actions'

interface SmartFcCrossReferenceLinkProps {
  children: ReactNode
  attribs: {
    class: string
    'data-flavour': string
    'data-focusedid': string
    'data-isqalink': string
    'data-rootid': string
    'data-sectionid': string
  }
}

const SmartFcCrossReferenceLink: FunctionComponent<SmartFcCrossReferenceLinkProps> = ({
  children,
  attribs
}) => {
  const dispatch = useDispatch()
  const isLogged = useSelector(selectIsLogged)
  const flavour = useSelector(selectFlavour)
  const [href, setHref] = useState<string>()
  const [hasAccessToReferenceSection, setHasAccessToReferenceSection] = useState<boolean>()
  const [isBrokenRef, setIsBrokenRef] = useState(false)
  const [brokenRefModalOpen, setBrokenRefModalOpen] = useState(false)

  const {
    'data-flavour': referenceFlavour,
    'data-rootid': referenceRootId,
    'data-sectionid': referenceSectionId,
    'data-focusedid': referenceFocusedId,
    'data-isqalink': isReferenceQaLink
  } = attribs

  const flavourForSections = referenceFlavour !== '' ? referenceFlavour : flavour

  const sections = useObservable(
    requestSections(flavourForSections),
    RESPONSE_PENDING
  )

  useEffect(() => {
    if (isValidResponse(sections)) {
      const sectionNode = sections.nodes[referenceFocusedId] ?? sections.nodes[referenceSectionId] ?? sections.nodes[referenceRootId]

      if (sectionNode == null) {
        setIsBrokenRef(true)
        return
      }

      const [sectionId, focusedId, highlight] = [...new Set([
        referenceRootId,
        referenceFocusedId,
        referenceFocusedId
      ])]

      const referenceHref = url(
        QA_CONTENT,
        {
          aol: sectionNode.areaOfLaw,
          flavour: referenceFlavour,
          sectionId,
          focusedId,
          highlight
        }
      )

      setHref(referenceHref)
      setHasAccessToReferenceSection(sectionNode.currentUserCanAccessSection)
    }
  }, [
    sections,
    referenceFlavour,
    referenceRootId,
    referenceSectionId,
    referenceFocusedId,
    isReferenceQaLink
  ])

  const handleOnClick = (): void => {
    if (isBrokenRef) {
      setBrokenRefModalOpen(true)
      return
    }
    if (isLogged && hasAccessToReferenceSection !== true && hasAccessToReferenceSection !== undefined) {
      dispatch(setUserRolesModalIsOpen(true))
    }
  }

  return (
    <>
      <a
        className={cx('fc-cross-reference', { wait: sections === RESPONSE_PENDING, pointer: isBrokenRef })}
        href={hasAccessToReferenceSection === true ? href : undefined}
        onClick={handleOnClick}
      >
        {children}
      </a>
      <BrokenRefDialog open={brokenRefModalOpen} onClose={() => setBrokenRefModalOpen(false)} />
    </>
  )
}

export default SmartFcCrossReferenceLink
